import type { StorageAccessPolicy, StorageResourceRef, StorageSasAdHocRequest, StorageSasPolicyRequest, StorageSasRequest } from '../../types/azureStorage';
import { normalizeSasPermissions, policyResourceOf, type StorageSasResourceKind } from './azureStorageSasParameters';
import { hnsSasResourceKind, hnsSasTargetName, SAS_USER_DELEGATION_SIGNER_LIMIT } from './azureStorageHnsSas';
import { hnsDirectoryDepth } from './azureStorageHnsPaths';

/** One word per SAS permission letter (the SAS dialog's checkbox labels and the MCP approval summary). */
export const SAS_PERMISSION_NAMES: Record<string, string> = {
  r: 'Read', l: 'List', a: 'Add', c: 'Create', w: 'Write', d: 'Delete', x: 'Delete version', t: 'Tags', m: 'Move',
  e: 'Execute', i: 'Immutability', y: 'Permanent delete', f: 'Filter', u: 'Update', p: 'Process',
};
/** S8: a directory SAS spends `o` / `p` on ownership and ACLs (a queue's `p` is Process). */
const DIRECTORY_PERMISSION_NAMES: Record<string, string> = { o: 'Change owner', p: 'Change permissions' };

/** The word for one letter of `kind`'s alphabet (the dialog's checkbox label and the approval summary). */
export const sasPermissionName = (kind: StorageSasResourceKind, letter: string): string =>
  (kind === 'directory' ? DIRECTORY_PERMISSION_NAMES[letter] : undefined) ?? SAS_PERMISSION_NAMES[letter] ?? letter;

/**
 * An upload (or a new folder, which only exists to upload into) under a SAS that grants neither Write (w) nor Create (c)
 * (release pass 2026-10-01: Upload stayed enabled on a list-only `sp=rl` SAS and failed with Azure's sentence, which
 * names no permission). `granted` = the SAS's `sp` letters when known (a stored-policy SAS keeps them in the policy).
 */
export function sasUploadRefusalText(granted: string | undefined): string {
  const grants = granted ? grantsLine(granted) : 'This SAS does not allow uploads';
  return `${grants}: uploading files and creating folders need the Write (w) or Create (c) permission. Use a SAS that includes one of them.`;
}

/** "This SAS grants Read, Create and List only (sp=rcl)" — the letters' names, never the token. */
function grantsLine(granted: string): string {
  const names = [...granted].map((letter) => SAS_PERMISSION_NAMES[letter] ?? letter);
  const listed = names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `This SAS grants ${listed} only (sp=${granted})`;
}

/**
 * An upload that would OVERWRITE an existing blob under a SAS with Create (c) but not Write (w) (review round 2): Create
 * writes a new blob only, and Azure refuses replacing an existing one with it (Put Blob / Put Block List, "Create a
 * service SAS" permission table), so the refusal names Write instead of "Write (w) or Create (c)", which the SAS has.
 */
export function sasOverwriteRefusalText(granted: string): string {
  return `${grantsLine(granted)}: Create (c) adds new blobs only, and overwriting an existing blob needs the Write (w) permission. Use a SAS that includes Write, or upload the file under another name.`;
}

/**
 * Why the file editor cannot save over an existing blob: a SAS whose letters are known and lack Write (w). Create (c)
 * adds new blobs only, so with it Save a copy still works; without both, the upload refusal says so.
 */
export function sasOverwriteRefusal(granted: string | undefined): string | undefined {
  if (granted === undefined || granted.includes('w')) return undefined;
  return granted.includes('c') ? `${grantsLine(granted)}: Create (c) adds new blobs only, and saving over an existing one needs the Write (w) permission. Use Save a copy, or a SAS that includes Write.` : sasUploadRefusalText(granted);
}

/** The upload refusal when the SAS's letters are known and include neither `w` nor `c`; otherwise none. */
export function sasUploadRefusal(granted: string | undefined): string | undefined {
  return granted !== undefined && !/[wc]/u.test(granted) ? sasUploadRefusalText(granted) : undefined;
}

/** Why a service / user-delegation SAS cannot target the account itself (Fable F5; the dialog's radio title and main's refusal). */
/**
 * Jam SQL's own limit, not Azure's: Azure documents a user-delegation SAS for Queue, Table and Azure Files as well
 * (Learn, "Create a user delegation SAS", 2026-09), but the app mints one only for these targets (`sasScopeGuard.ts`).
 */
export const USER_DELEGATION_TARGETS_REASON = 'Jam SQL creates a user-delegation SAS only for a blob container, a blob or a Data Lake path.';
export const SAS_KIND_NEEDS_RESOURCE = 'A service or user-delegation SAS needs a container, blob, table or queue. Select one first, or choose Account.';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const RISKY_VALIDITY_MS = 7 * DAY;
/** Letters that let the holder change or delete data (add, create, write, delete, delete version, tags, move, immutability, permanent delete, update, process / permissions, owner). */
const DATA_CHANGING = 'acwdxtmiyupo';
const KIND_LABEL = { account: 'an account SAS', service: 'a service SAS', 'user-delegation': 'a user-delegation SAS' } as const;

const count = (value: number, unit: string) => `${value.toLocaleString('en-US')} ${unit}${value === 1 ? '' : 's'}`;
const utc = (iso: string) => `${new Date(iso).toISOString().slice(0, 16).replace('T', ' ')} UTC`;
const days = (ms: number) => Math.round(ms / DAY);

function relative(iso: string, now: Date): string {
  const delta = Date.parse(iso) - now.getTime();
  const size = Math.abs(delta);
  if (size < MINUTE) return 'now';
  const years = size >= 2 * 365 * DAY ? `, about ${Math.round(size / (365.25 * DAY))} years` : '';
  const text = size < 2 * HOUR ? count(Math.round(size / MINUTE), 'minute') : size < 2 * DAY ? count(Math.round(size / HOUR), 'hour') : count(days(size), 'day');
  return delta > 0 ? `in ${text}${years}` : `${text} ago${years}`;
}

const unsignable = (kind: string): never => { throw new Error(`A SAS cannot target a ${kind} ref.`); };

/** Exhaustive (S7): every ref kind names itself; only the account ref reads as the whole account. */
function targetName(resource: StorageResourceRef): string {
  switch (resource.kind) {
    case 'account': return 'the whole storage account';
    case 'container': return `container ${resource.container}`;
    case 'blob': return `blob ${resource.container}/${resource.name}`;
    case 'table': return `table ${resource.table}`;
    case 'queue': return `queue ${resource.queue}`;
    case 'share': return `share ${resource.share}`;
    case 'file': return `file ${resource.share}/${resource.path}`;
    case 'hnsPath': return hnsSasTargetName(resource); // S8
    case 'service': case 'blobPrefix': case 'entity': case 'message': case 'directory': return unsignable(resource.kind);
    default: return unsignable((resource as { kind: never }).kind);
  }
}

/** The letter alphabet of the signed resource; `sasRequestProblem` refused every other kind before a summary exists. */
function letterKind(request: StorageSasRequest): StorageSasResourceKind {
  const resource = request.resource;
  switch (resource.kind) {
    case 'account': case 'container': case 'blob': case 'table': case 'queue': case 'share': case 'file': return resource.kind;
    case 'hnsPath': return hnsSasResourceKind(resource); // S8: its directory / container / blob alphabet
    case 'service': case 'blobPrefix': case 'entity': case 'message': case 'directory': return unsignable(resource.kind);
    default: return unsignable((resource as { kind: never }).kind);
  }
}

function spelled(kind: StorageSasResourceKind, letters: string): { letters: string; words: string; mutating: string[] } {
  const normalized = normalizeSasPermissions(kind, letters);
  const value = normalized.ok ? normalized.value : letters;
  const names = [...value].map((letter) => sasPermissionName(kind, letter));
  const mutating = [...value].filter((letter) => DATA_CHANGING.includes(letter)).map((letter) => sasPermissionName(kind, letter));
  return { letters: value, words: `${names.join(', ')} (${value})`, mutating };
}

/** What an approval describes: an ad-hoc request, or a stored-policy request with the policy read before asking (review R2). */
export type SasApprovalRequest = StorageSasAdHocRequest | (StorageSasPolicyRequest & { approvedPolicy: StorageAccessPolicy });
type PolicyField = { value?: string; source: string; fromPolicy: boolean };

/** Review E3: each field resolves on its own — the policy's value when it sets one, else the token's. */
function policyField(request: StorageSasPolicyRequest & { approvedPolicy: StorageAccessPolicy }, field: 'permissions' | 'startsOn' | 'expiresOn'): PolicyField {
  const own = request.approvedPolicy[field];
  if (own) return { value: own, source: `from policy "${request.identifier}"`, fromPolicy: true };
  return { value: request.supplement?.[field] || undefined, source: `set on the token; policy "${request.identifier}" sets none`, fromPolicy: false };
}

const when = (iso: string, now: Date) => `${utc(iso)} (${relative(iso, now)})`;

/**
 * The MCP approval summary for `storage_generate_sas` (Fable F1), built from a request `sasRequestProblem` accepted.
 * Warnings come first so the risky part of a grant is the first thing the approver reads. A stored-policy request
 * carries the policy main read before asking (review R2), so the summary shows the permissions, start and expiry the
 * token will have, each with its source, and the same warnings as an ad-hoc SAS. Carries no secret: the request holds
 * none (the signature is made only after approval).
 */
export function sasApprovalSummary(request: SasApprovalRequest, now: Date): string[] {
  const kind = letterKind(request);
  const target = targetName(request.resource);
  const adHoc = request.mode === 'ad-hoc' ? request : undefined;
  const policy = request.mode === 'policy' ? request : undefined;
  const fields = policy && { permissions: policyField(policy, 'permissions'), startsOn: policyField(policy, 'startsOn'), expiresOn: policyField(policy, 'expiresOn') };
  const letters = adHoc?.permissions ?? fields?.permissions.value;
  // A policy's own letters are its resource's letters — a container's even when the token targets one blob (policyGrantProblem).
  const permissions = letters ? spelled(fields?.permissions.fromPolicy ? policyResourceOf(request.resource)?.kind ?? kind : kind, letters) : undefined;
  const expiresOn = adHoc?.expiresOn ?? fields?.expiresOn.value;
  const startsOn = adHoc?.startsOn ?? fields?.startsOn.value;
  const warnings: string[] = [];
  if (request.kind === 'account') warnings.push('Warning: an account SAS covers the whole storage account, not one container or blob.');
  if (permissions?.mutating.length) warnings.push(`Warning: the token can change or delete data (${permissions.mutating.join(', ')}).`);
  // A token for one blob or one file cannot list, even under a policy whose letters include List.
  if (permissions?.letters.includes('l') && kind !== 'blob' && kind !== 'file') {
    warnings.push(kind === 'account' ? 'Warning: the token can list the contents of the storage account.'
      : kind === 'share' ? `Warning: the token can list every file and folder in ${target}.`
        : kind === 'directory' ? `Warning: the token can list every file and directory under ${target}.` : `Warning: the token can list every blob in ${target}.`);
  }
  const validFor = expiresOn ? Date.parse(expiresOn) - now.getTime() : 0;
  if (validFor > RISKY_VALIDITY_MS) {
    const revoke = policy ? `Deleting policy "${policy.identifier}" revokes it` : 'An ad-hoc SAS can be revoked only by rotating the account key';
    warnings.push(`Warning: the token stays valid for ${count(days(validFor), 'day')} (more than 7). ${revoke}.`);
  }
  const lines = [...warnings, `Generate ${KIND_LABEL[request.kind]} ${policy ? `under stored access policy "${policy.identifier}"` : '(ad-hoc)'} for ${target}`];
  if (adHoc?.kind === 'account') lines.push(`Services: ${(adHoc.services ?? []).join(', ')}; resource types: ${(adHoc.resourceTypes ?? []).join(', ')}`);
  if (kind === 'directory' && request.resource.kind === 'hnsPath') lines.push(`Directory depth: ${hnsDirectoryDepth(request.resource.path)} (sdd) — the token covers ${target} and everything under it, nothing outside it`); // S8
  if (fields) {
    if (permissions) lines.push(`Permissions (${fields.permissions.source}): ${permissions.words}`);
    lines.push(startsOn ? `Starts (${fields.startsOn.source}): ${when(startsOn, now)}` : 'Starts: now (no start time set)');
    if (expiresOn) lines.push(`Expires (${fields.expiresOn.source}): ${when(expiresOn, now)}`);
  } else {
    if (permissions) lines.push(`Permissions: ${permissions.words}`);
    if (expiresOn) lines.push(`Valid ${startsOn ? `from ${when(startsOn, now)}` : 'from now'} until ${when(expiresOn, now)}`);
  }
  if (request.protocol) lines.push(`Protocol: ${request.protocol === 'https' ? 'HTTPS only' : 'HTTPS and HTTP'}`);
  if (request.ipRange) lines.push(request.ipRange.end ? `IP addresses: ${request.ipRange.start} to ${request.ipRange.end}` : `IP address: ${request.ipRange.start}`);
  if (policy) lines.push(`Changing or deleting policy "${policy.identifier}" later changes or revokes this token.`);
  if (request.kind === 'user-delegation' && kind === 'directory') lines.push(SAS_USER_DELEGATION_SIGNER_LIMIT);
  lines.push('The token stays in Jam SQL Studio; the agent receives non-secret facts only.');
  return lines;
}
